import React from "react";
import classnames from "classnames";
import _ from "lodash";
import DeskingHelper, {NO_TIER, bestRankedPerLender, groupLabelFamilies, isCaptive, lenderColumn, programName, quotePassesCreditScore} from "../../utils/DeskingHelper";
import {DeskingTableHeaders} from "./DeskingTableHeaders";
import {DeskingTableRow} from "./DeskingTableRow";

// Decide whether a term passes the Terms filter (range / specific).
const isTermVisible = (term, { termMode, termRange, selectedTerms }) => {
    const n = Number(term)
    if (termMode === 'specific') {
        return selectedTerms.length === 0 || selectedTerms.includes(term)
    }
    const min = String(termRange.min).trim() === '' ? -Infinity : Number(termRange.min)
    const max = String(termRange.max).trim() === '' ? Infinity : Number(termRange.max)
    return n >= min && n <= max
}

// A lender's best (lowest) monthly payment across every term/tier in the matrix.
// Used to order the lender columns when "Best Payment" sort is on.
// monthly_payment is a thousands-separated string (e.g. "1,486.20"), so strip
// non-numeric chars before parsing — Number("1,486.20") is NaN.
const toNumber = (v) => parseFloat(String(v ?? '').replace(/[^0-9.-]/g, ''))

// Sort a row's quotes (object {lender: quote}) ascending by payment, into an
// array of {lender, quote}. Used by "Best Payment" mode, where each column is a
// rank (best first) rather than a lender, so the lender rides on the cell.
const sortRowByBestPayment = (rowQuotes) =>
    Object.entries(rowQuotes)
        .map(([lender, quote]) => ({ lender, quote }))
        .sort((a, b) => toNumber(a.quote.monthly_payment) - toNumber(b.quote.monthly_payment))

export const DeskingTable = ( { dealState, dealId, onSave, filters } ) => {
    // Credit Score: ineligible quotes are no longer dropped — they stay in the
    // grid (disabled + dimmed) so users can still see them. `isCreditIneligible`
    // flags a quote whose band excludes the entered score; the cell renders it
    // disabled with an explanatory tooltip.
    const creditScore = String(filters.creditScore ?? '').trim()
    const hasCreditFilter = creditScore !== ''
    const creditScoreNum = Number(creditScore)
    const isCreditIneligible = (quote) =>
        hasCreditFilter && !quotePassesCreditScore(quote, creditScoreNum)

    // Captive Only removes every other lender; the response keeps them. Ignored
    // when no quote is captive (the toggle is hidden then).
    const captiveOnly = filters.captiveOnly && dealState.quotes?.some(isCaptive)
    const shownQuotes = captiveOnly ? dealState.quotes.filter(isCaptive) : dealState.quotes

    // Best By Lender: keep each lender's best-ranked offer per term and tier, then
    // show it under its program's column, as By Lender does. The response keeps
    // every offer.
    const bestOfferPerLender = filters.bestOfferPerLender
    const helper = new DeskingHelper(bestOfferPerLender ? bestRankedPerLender(shownQuotes) : shownQuotes, dealState)

    const realTiers = helper.tiers()          // present tiers, best (T1) first
    const dataHasTiers = realTiers.length > 0
    const matrix = helper.quotesByTermTierLender()
    const captiveSet = helper.captiveLenders()

    const bestByLender = filters.bestByLender
    // Best Payment sort: each row's quotes are sorted by payment and the columns
    // become ranks (no lender headers; the lender is shown on each cell).
    const sortByBestPayment = filters.sortByBestPayment

    // Best by Lender (default): collapse to just the best tier per lender and
    // hide the Tier column. All Tiers: show every tier with the column. Degrade
    // (no tiers in the data): a single synthetic row, no column either way.
    const showTierColumn = dataHasTiers && !bestByLender
    const useBestByLender = bestByLender && dataHasTiers

    // Best Payment mode collapses each lender to ONE row; the real tier is hidden,
    // so a synthetic key labels that single row.
    const BEST_TIER = '__best__'
    const displayTiers = !dataHasTiers
        ? [NO_TIER]
        : useBestByLender
            ? [BEST_TIER]
            : realTiers

    const visibleTerms = helper.terms().filter((term) => isTermVisible(term, filters))

    // The matrix actually rendered. In Best Payment mode each lender shows a single
    // best-*eligible* quote per term: the lowest payment the entered credit score
    // qualifies for. (No score → its outright best, i.e. the top tier, as before.)
    // When a lender has nothing eligible at a term we keep its best quote so the
    // column doesn't vanish — it then renders dimmed via the cell's ineligible
    // styling. Every other mode renders the raw matrix unchanged.
    const displayMatrix = useBestByLender
        ? (() => {
            const out = {}
            visibleTerms.forEach((term) => {
                const perLender = {}   // competitor → [column, quote]
                realTiers.forEach((tier) => {
                    const cell = matrix[term]?.[tier] || {}
                    Object.entries(cell).forEach(([lender, quote]) => {
                        if (!quote) return
                        // Best By Lender: a lender's program columns compete for its one quote.
                        const competitor = bestOfferPerLender ? quote.lender_name : lender
                        const prev = perLender[competitor]?.[1]
                        const eligible = !isCreditIneligible(quote)
                        const prevEligible = prev && !isCreditIneligible(prev)
                        // Prefer eligible over ineligible; within the same
                        // eligibility, the lower payment wins.
                        const better =
                            !prev ||
                            (eligible && !prevEligible) ||
                            (eligible === prevEligible &&
                                toNumber(quote.monthly_payment) < toNumber(prev.monthly_payment))
                        if (better) perLender[competitor] = [lender, quote]
                    })
                })
                out[term] = { [BEST_TIER]: Object.fromEntries(Object.values(perLender)) }
            })
            return out
        })()
        : matrix

    // Each captive program's lowest eligible payment at every term shown, from all
    // offers, so Best By Lender orders its program columns as By Lender does.
    const termPayments = {}   // column → { term → payment }
    dealState.quotes?.forEach((quote) => {
        const payment = toNumber(quote.monthly_payment)
        if (!isCaptive(quote) || !visibleTerms.includes(quote.term) || isCreditIneligible(quote) || !(payment > 0)) return
        const byTerm = (termPayments[lenderColumn(quote)] ||= {})
        byTerm[quote.term] = Math.min(byTerm[quote.term] ?? Infinity, payment)
    })
    const lowestShownPayment = (lender) => Math.min(...Object.values(termPayments[lender] || {}))

    // Captive columns lead, cheapest first, compared like for like: a program loses
    // each term where another captive program is cheaper. Fewest losses first, then
    // the lowest payment shown, then best-ranked, program-family order, so a
    // program alone at a longer term can't lead on that payment. A program with
    // no eligible payment shown loses nothing, so it goes after all of them. The
    // rest keep their natural order. Headers and cells both follow `lenders`.
    const rawLenders = helper.lenders()
    const captiveColumns = groupLabelFamilies(rawLenders.filter((l) => captiveSet.has(l)))
    const termLosses = _.countBy(visibleTerms.flatMap((term) => {
        const offered = Object.entries(termPayments)
            .map(([lender, byTerm]) => [lender, byTerm[term] ?? Infinity])
            .filter(([, payment]) => payment < Infinity)
        const cheapest = Math.min(...offered.map(([, payment]) => payment))
        return offered.filter(([, payment]) => payment > cheapest).map(([lender]) => lender)
    }))
    const lenders = [
        ..._.sortBy(captiveColumns, [
            (lender) => lowestShownPayment(lender) === Infinity,
            (lender) => termLosses[lender] || 0,
            (lender) => lowestShownPayment(lender),
        ]),
        ...rawLenders.filter((l) => !captiveSet.has(l)),
    ]

    // Best Payment mode uses a fixed number of rank columns (= the most quotes in
    // any visible row). Rows with fewer quotes get empty fillers, so every row is
    // full-width — that keeps the boxes uniform and the tier borders span the row.
    const maxColumns = sortByBestPayment
        ? Math.max(1, ...visibleTerms.flatMap((term) =>
            displayTiers.map((tier) => Object.keys(displayMatrix[term]?.[tier] || {}).length)
          ))
        : 0

    // ── Lowest-payment highlights ──
    // Blue rim on the single lowest OVERALL payment and the single lowest CAPTIVE
    // payment across the whole visible grid. If the lowest overall is itself a
    // captive, the two coincide and only one box is rimmed.
    const lowestPaymentKeys = (() => {
        let overall = null
        let captive = null
        const consider = (entry) => {
            const p = toNumber(entry.quote.monthly_payment)
            if (!(p > 0)) return
            if (!overall || p < toNumber(overall.quote.monthly_payment)) overall = entry
            if (captiveSet.has(entry.lender) &&
                (!captive || p < toNumber(captive.quote.monthly_payment))) captive = entry
        }
        visibleTerms.forEach((term) => {
            displayTiers.forEach((tier) => {
                const cell = displayMatrix[term]?.[tier] || {}
                // Column order, so an equal payment rims the leftmost column.
                lenders.map((lender) => [lender, cell[lender]]).forEach(([lender, quote]) => {
                    // Only eligible quotes can win the green rim.
                    if (quote && !isCreditIneligible(quote)) {
                        consider({ term, tier, lender, quote })
                    }
                })
            })
        })
        const keyOf = (e) => `${e.term}|${e.tier}|${e.lender}`
        return new Set([overall, captive].filter(Boolean).map(keyOf))
    })()

    return (
        <div className={classnames('desking-scroll-container', {
            'best-payment': sortByBestPayment,
            // Best Payment chips add a program line for captive offers.
            'has-programs': sortByBestPayment && dealState.quotes?.some(programName),
            // Best-by-Lender (one tier per lender) → fewer rows, so chips enlarge
            // to a 15px payment. "All Tiers" stays dense at 13px.
            'enlarged': bestByLender,
            // Finance (purchase) deals show APR on each card — row heights expand.
            'is-purchase': dealState.is_purchase,
        })} style={{ '--grid-columns': sortByBestPayment ? maxColumns : lenders.length }}>
            <table>
                <DeskingTableHeaders lenders={lenders} hasTiers={showTierColumn} sortByBestPayment={sortByBestPayment} maxColumns={maxColumns}/>
                <tbody>
                {
                    visibleTerms.map((term) => {
                        // Only render a (term, tier) row when it has at least one
                        // quote — drops empty tiers, keeps the ones that exist.
                        const rowsTiers = displayTiers.filter((tier) =>
                            Object.keys(displayMatrix[term]?.[tier] || {}).length > 0
                        )
                        if (rowsTiers.length === 0) return null

                        return rowsTiers.map((tier, idx) => {
                            const rowQuotes = displayMatrix[term]?.[tier] || {}
                            return (
                                <DeskingTableRow
                                    key={`${term}-${tier}`}
                                    term={term}
                                    tier={tier}
                                    hasTiers={showTierColumn}
                                    isFirstTierRow={idx === 0}
                                    isGroupStart={idx === 0}
                                    quotesForRow={rowQuotes}
                                    bestPaymentQuotes={sortByBestPayment ? sortRowByBestPayment(rowQuotes) : null}
                                    sortByBestPayment={sortByBestPayment}
                                    maxColumns={maxColumns}
                                    lenders={lenders}
                                    lowestPaymentKeys={lowestPaymentKeys}
                                    creditScore={filters.creditScore}
                                    dealState={dealState}
                                    dealId={dealId}
                                    onSave={onSave}
                                />
                            )
                        })
                    })
                }
                </tbody>
            </table>
        </div>
    )
}
